import React, { useMemo, useRef } from "react";
import RevealShell from "./reveal/RevealShell";
import WinnerProcessingBox from "./reveal/WinnerProcessingBox";
import { revealAnimationFor } from "./reveal/animations";
import "../../styles/winnerReveal.css";

/**
 * The closing seconds of a round, as a show.
 *
 * A round settles at its bet close, a few seconds before its clock runs
 * out, and those seconds used to be a "please wait" box over a dead
 * board. They now hold the draw, in whichever style the game is set to.
 *
 * The one rule the shows are built around: the winner is not public
 * until the round's clock reaches zero. The server withholds it (see
 * today-last-draw and running-game), so `winnerIds` arrives empty and
 * fills in later - the animation spins knowing nothing and only lands
 * when the answer turns up. That also means the landing and the
 * celebration play over the first seconds of the next round, which is
 * why the page keeps this mounted past the window that opened it.
 */

export default function WinnerReveal({
  visible,
  remainingSeconds,
  betCloseSeconds,
  tiles,
  winnerIds,
  animation,
  runId,
  onDone,
  closedLabel,
}) {
  const incoming = useMemo(
    () =>
      (Array.isArray(tiles) ? tiles : []).filter(
        (t) => t && t.id != null && t.img,
      ),
    [tiles],
  );

  // The board the show is playing on. The page rebuilds its yantra list
  // on every refresh, and for a moment between a round ending and the
  // next one's board arriving it is empty - which used to unmount the
  // whole reveal and mount it again, restarting the animation from the
  // top. On screen that read as the modal closing and reopening.
  const lastBoardRef = useRef([]);
  if (incoming.length) lastBoardRef.current = incoming;
  const board = incoming.length ? incoming : lastBoardRef.current;

  const winnerIndex = useMemo(() => {
    const first = (Array.isArray(winnerIds) ? winnerIds : [])
      .map((id) => Number(id))
      .find((n) => Number.isFinite(n) && n > 0);
    if (!first || !board.length) return -1;
    return board.findIndex((t) => Number(t.id) === first);
  }, [winnerIds, board]);

  const show = revealAnimationFor(animation);
  const Animation = show.component;

  // The default: the plain box, no draw - it needs no board and closes
  // itself when the clock runs out.
  if (show.classic) {
    if (!visible) return null;
    return (
      <WinnerProcessingBox
        key={runId ?? "processing"}
        remainingSeconds={remainingSeconds}
        betCloseSeconds={betCloseSeconds}
        closedLabel={closedLabel}
        onFinished={() => {
          if (typeof onDone === "function") onDone();
        }}
      />
    );
  }

  if (!visible || !board.length) return null;

  return (
    <RevealShell
      title={show.title}
      status={
        winnerIndex >= 0
          ? "Result"
          : `${show.status} • purchasing closed for the last ${betCloseSeconds} seconds`
      }
      remainingSeconds={remainingSeconds}
    >
      <Animation
        // One mount per draw, keyed on the round it belongs to. Keying on
        // anything that can change mid-show - the style, the board size -
        // restarted the animation underneath itself, and a show that keeps
        // restarting never reaches its own ending, so the modal stayed up.
        key={runId ?? "reveal"}
        tiles={board}
        winnerIndex={winnerIndex}
        onFinished={() => {
          if (typeof onDone === "function") onDone();
        }}
      />
    </RevealShell>
  );
}
